import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import './App.css';

const stripePromise = loadStripe('pk_test_51SFe3VGSXAViqFcQnsdax2I8fBUKfPaWfbl6cV4gU9EYsnwiSuOwxZoJQOOvEtlcWh1xOIzodkR2neqE85UQcQGo00O89tpobk');

function App() {
  const [file, setFile] = useState(null);
  const [state, setState] = useState('');
  const [violationType, setViolationType] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);

  const BACKEND_URL = 
'https://traffic-defense-backend-production.up.railway.app';

  const US_STATES = [
    'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado',
    'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho',
    'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana',
    'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota',
    'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada',
    'New Hampshire', 'New Jersey', 'New Mexico', 'New York',
    'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon',
    'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
    'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington',
    'West Virginia', 'Wisconsin', 'Wyoming'
  ];

  const VIOLATION_TYPES = [
    'Speeding', 'Red Light', 'Stop Sign', 'Parking',
    'Reckless Driving', 'DUI/DWI', 'No Insurance',
    'Expired Registration', 'Cell Phone Use',
    'Seatbelt Violation', 'Other'
  ];

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      console.log('File selected:', selectedFile.name);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file || !state || !violationType) {
      alert('Please fill all fields and upload your ticket');
      return;
    }
    setAnalyzing(true);
    const formData = new FormData();
    formData.append('ticket', file);
    formData.append('state', state);
    formData.append('violationType', violationType);
    try {
      const response = await fetch(BACKEND_URL + '/api/create-payment', {
        method: 'POST',
        body: formData,
      });
      if (!response.ok) throw new Error('Server error');
      const data = await response.json();
      setClientSecret(data.clientSecret);
    } catch (error) {
      console.error('Error:', error);
      alert('Error processing ticket. Please try again.');
      setAnalyzing(false);
    }
  };

  return (
    <div className="App">
      <header className="hero">
        <div className="container">
          <h1>🚗 TrafficDefense.ai</h1>
          <p className="tagline">Beat Your Traffic Ticket in 10 Minutes</p>
          <div className="stats">
            <div className="stat">
              <span className="stat-number">67%</span>
              <span className="stat-label">Success Rate</span>
            </div>
            <div className="stat">
              <span className="stat-number">$327</span>
              <span className="stat-label">Avg. Saved</span>
            </div>
            <div className="stat">
              <span className="stat-number">8,432</span>
              <span className="stat-label">Tickets Fought</span>
            </div>
          </div>
        </div>
      </header>
      <main className="main-content">
        <div className="container">
          {!clientSecret && !result && (
            <>
              <section className="how-it-works">
                <h2>How It Works</h2>
                <div className="steps">
                  <div className="step">
                    <div className="step-number">1</div>
                    <h3>Upload Your Ticket</h3>
                    <p>Take a photo or scan your traffic ticket</p>
                  </div>
                  <div className="step">
                    <div className="step-number">2</div>
                    <h3>AI Analysis</h3>
                    <p>Our AI finds errors, defenses, and winning strategies</p>
                  </div>
                  <div className="step">
                    <div className="step-number">3</div>
                    <h3>Get Your Defense</h3>
                    <p>Professional letter + court prep guide instantly</p>
                  </div>
                </div>
              </section>
              <section className="upload-section">
                <div className="upload-card">
                  <h2>Fight Your Ticket Now</h2>
                  <p className="price">$39.99 <span className="price-detail">vs $300+ for a lawyer</span></p>
                  <form onSubmit={handleSubmit}>
                    <div className="form-group">
                      <label>Upload Your Ticket</label>
                      <input 
                        type="file" 
                        accept="image/*,.pdf,.txt"
                        onChange={handleFileChange}
                        required
                        key={file ? file.name : 'empty'}
                      />
                      {file && <p style={{color: '#48bb78', marginTop: '8px', fontSize: '14px'}}>✓ {file.name}</p>}
                    </div>
                    <div className="form-group">
                      <label>Select Your State</label>
                      <select value={state} onChange={(e) => setState(e.target.value)} required>
                        <option value="">Choose State...</option>
                        {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Violation Type</label>
                      <select value={violationType} onChange={(e) => setViolationType(e.target.value)} required>
                        <option value="">Choose Type...</option>
                        {VIOLATION_TYPES.map(v => <option key={v} value={v}>{v}</option>)}
                      </select>
                    </div>
                    <button type="submit" className="btn-primary" disabled={analyzing}>
                      {analyzing ? 'Processing...' : 'Analyze My Ticket → $39.99'}
                    </button>
                  </form>
                  <p className="disclaimer">⚠️ This tool provides legal information, not legal advice. We are not a law firm.</p>
                </div>
              </section>
            </>
          )}
          {clientSecret && !result && (
            <section className="payment-section">
              <div className="payment-card">
                <h2>Complete Your Payment</h2>
                <p>Get instant access to your defense package</p>
                <Elements stripe={stripePromise} options={{ clientSecret }}>
                  <CheckoutForm 
                    clientSecret={clientSecret} 
                    backendUrl={BACKEND_URL} 
                    onSuccess={(analysisResult) => { 
                      setResult(analysisResult); 
                      setClientSecret(''); 
                      setAnalyzing(false); 
                    }} 
                  />
                </Elements>
              </div>
            </section>
          )}
          {result && (
            <section className="results-section">
              <div className="results-card">
                <h2>✅ Your Defense Package is Ready!</h2>
                <div className="result-block">
                  <h3>📊 Ticket Analysis</h3>
                  <div className="analysis-content">
                    <p><strong>Success Probability:</strong> {result.successRate}%</p>
                    <p><strong>Recommended Strategy:</strong> {result.strategy}</p>
                    <div className="defenses">
                      <h4>Possible Defenses Found:</h4>
                      <ul>{result.defenses.map((def, i) => <li key={i}>{def}</li>)}</ul>
                    </div>
                  </div>
                </div>
                <div className="result-block">
                  <h3>📝 Your Defense Letter</h3>
                  <div className="letter-preview"><pre>{result.defenseLetter}</pre></div>
                  <button className="btn-secondary">Download Letter (PDF)</button>
                </div>
                <div className="result-block">
                  <h3>⚖️ Court Preparation Guide</h3>
                  <div className="court-prep">
                    <h4>What to Say in Court:</h4>
                    <p>{result.courtScript}</p>
                    <h4>Questions to Ask the Officer:</h4>
                    <ul>{result.officerQuestions.map((q, i) => <li key={i}>{q}</li>)}</ul>
                    <h4>Evidence to Bring:</h4>
                    <ul>{result.evidence.map((e, i) => <li key={i}>{e}</li>)}</ul>
                  </div>
                </div>
                <div className="result-block">
                  <h3>📍 {state} Specific Information</h3>
                  <p><strong>Filing Deadline:</strong> {result.stateInfo.deadline}</p>
                  <p><strong>Court Address:</strong> {result.stateInfo.courtAddress}</p>
                  <p><strong>Relevant Laws:</strong> {result.stateInfo.laws}</p>
                </div>
                <button className="btn-primary" onClick={() => window.print()}>Print Complete Package</button>
              </div>
            </section>
          )}
          <section className="features">
            <h2>Why TrafficDefense.ai?</h2>
            <div className="feature-grid">
              <div className="feature">
                <span className="feature-icon">⚡</span>
                <h3>Instant Analysis</h3>
                <p>Get your defense strategy in under 10 minutes</p>
              </div>
              <div className="feature">
                <span className="feature-icon">💰</span>
                <h3>Save Hundreds</h3>
                <p>$39.99 vs $300-1,000 for a traffic lawyer</p>
              </div>
              <div className="feature">
                <span className="feature-icon">🎯</span>
                <h3>Find Every Defense</h3>
                <p>AI spots errors officers miss 67% of the time</p>
              </div>
              <div className="feature">
                <span className="feature-icon">📋</span>
                <h3>Complete Package</h3>
                <p>Letter + court prep + state-specific guidance</p>
              </div>
            </div>
          </section>
        </div>
      </main>
      <footer className="footer">
        <div className="container">
          <p>&copy; 2025 TrafficDefense.ai - All Rights Reserved</p>
          <div className="footer-links">
            <a href="/terms.html">Terms of Service</a>
            <a href="/privacy.html">Privacy Policy</a>
            <a href="mailto:support@trafficdefense.ai">Contact</a>
          </div>
          <p className="legal-disclaimer">This service provides legal information, not legal advice.</p>
        </div>
      </footer>
    </div>
  );
}

function CheckoutForm({ clientSecret, backendUrl, onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    const { error, paymentIntent } = await stripe.confirmPayment({ 
      elements, 
      redirect: 'if_required' 
    });
    if (error) {
      alert(error.message);
      setProcessing(false);
    } else if (paymentIntent.status === 'succeeded') {
      const response = await fetch(backendUrl + '/api/get-analysis/' + paymentIntent.id);
      const data = await response.json();
      onSuccess(data.analysis);
    }
  };
  
  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement />
      <button 
        type="submit" 
        disabled={!stripe || processing} 
        className="btn-primary" 
        style={{ marginTop: '20px' }}
      >
        {processing ? 'Processing...' : 'Pay $39.99 & Get Defense'}
      </button>
    </form>
  );
}

export default App;
